import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Stack, TextField, Typography } from "@mui/material";
import { adminFetch } from "../services/adminFetch";
import { Colors } from "../utils/colors";

const MAX_BYTES = 12000;
const validPage = page => typeof page?.content === "string" && Number.isSafeInteger(page.revision) && page.revision >= 0;
export default function AboutUsEditor({ canEdit }) {
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [revision, setRevision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const inFlight = useRef(false);
  const byteCount = new Blob([content]).size;
  // eslint-disable-next-line no-control-regex -- Match the Backend plain-text control-character contract.
  const invalid = !content.trim() || byteCount > MAX_BYTES || /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(content);
  const unchanged = content.trim() === savedContent.trim();
  const load = useCallback(async () => {
    if (!canEdit || inFlight.current) return;
    inFlight.current = true; setBusy(true); setNotice(null); setRevision(null);
    try {
      const data = await adminFetch("/api/admin/settings/about-us");
      if (data?.status !== true || !validPage(data.page)) {
        setNotice({ severity: "error", text: `Unable to load About Us. ${data?.message || "Reload to try again."}` });
        return;
      }
      setContent(data.page.content); setSavedContent(data.page.content); setRevision(data.page.revision);
      setNotice({ severity: "success", text: "About Us loaded." });
    } catch (_) { setNotice({ severity: "error", text: "Unable to load About Us. Reload to try again." }); }
    finally { inFlight.current = false; setBusy(false); }
  }, [canEdit]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!canEdit || inFlight.current || revision === null || invalid || unchanged) return;
    inFlight.current = true; setBusy(true); setNotice(null);
    const next = content.trim();
    try {
      const data = await adminFetch("/api/admin/settings/about-us", {
        method: "PUT", body: JSON.stringify({ content, expected_revision: revision })
      });
      if (data?.status !== true) {
        setNotice({ severity: "error", text: data?.message || "Save outcome unknown. Reload content to verify before editing again; retrying unchanged content is safe." });
        return;
      }
      if (!validPage(data.page) || data.page.content !== next || (!data.page.unchanged && !data.page.audit_id)) {
        setNotice({ severity: "error", text: "Save outcome not confirmed. Reload saved content to verify before editing again." });
        return;
      }
      setContent(data.page.content); setSavedContent(data.page.content); setRevision(data.page.revision);
      setNotice({ severity: "success", text: data.page.unchanged ? "Content already saved; no new audit entry was needed." : `About Us saved. Audit ID: ${data.page.audit_id}` });
    } catch (_) { setNotice({ severity: "error", text: "Save outcome unknown. Reload content to verify before editing again." }); }
    finally { inFlight.current = false; setBusy(false); }
  };
  if (!canEdit) return null;
  return <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
    <Stack spacing={2}>
      <Typography variant="h6" component="h2" sx={{ color: Colors.accent }}>About Us page</Typography>
      <TextField label="About Us content" value={content} onChange={event => setContent(event.target.value)} multiline minRows={12} fullWidth disabled={busy || revision === null} error={byteCount > MAX_BYTES} helperText={`${Array.from(content).length} characters · ${byteCount}/${MAX_BYTES} UTF-8 bytes. Plain text only.`} />
      {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Button variant="contained" onClick={save} disabled={busy || revision === null || invalid || unchanged}>Save About Us</Button>
        <Button onClick={load} disabled={busy}>Reload saved content</Button>
      </Stack>
    </Stack>
  </Card>;
}
